// ===========================================
// BUSTED! BLACKJACK - UNIVERSAL MODAL SHELL
// ===========================================
// Single source of truth for modal frame styling.
// All modals use this shell for consistent appearance.
// ===========================================

import { useEffect, useCallback } from 'react';
import { MODAL_FRAME, ModalVariant } from '@busted-blackjack/shared';

// ===========================================
// TYPES
// ===========================================

export interface ModalProps {
  /** Variant: 'default' (gold) or 'alert' (red) */
  variant?: ModalVariant;
  /** true = black/80 scrim + captures input; false = transparent passthrough */
  blocking?: boolean;
  /** Renders standard header if present */
  title?: string;
  /** Subtitle below title */
  subtitle?: string;
  /** Show close button (default = !!onClose) */
  showClose?: boolean;
  /** Close when clicking overlay (default true) */
  closeOnOverlayClick?: boolean;
  /** Close on ESC key (default true) */
  closeOnEsc?: boolean;
  /** Close callback */
  onClose?: () => void;
  /** Max width class (e.g. 'max-w-lg', 'max-w-4xl', 'w-80') */
  maxWidthClass?: string;
  /** Body content */
  children: React.ReactNode;
}

// ===========================================
// CLOSE BUTTON SVG
// ===========================================

function CloseButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

// ===========================================
// MODAL COMPONENT
// ===========================================

export function Modal({
  variant = 'default',
  blocking = true,
  title,
  subtitle,
  showClose,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  onClose,
  maxWidthClass = 'max-w-lg',
  children,
}: ModalProps) {
  // Default showClose to !!onClose if not explicitly set
  const shouldShowClose = showClose ?? !!onClose;

  // ESC key handler
  useEffect(() => {
    if (!closeOnEsc || !onClose) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose?.();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOnEsc, onClose]);

  // Overlay click handler
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && blocking && closeOnOverlayClick && onClose) {
        onClose();
      }
    },
    [blocking, closeOnOverlayClick, onClose]
  );

  // z-index: default = z-50, alert = z-[9999]
  const zIndex = variant === 'alert' ? 'z-[9999]' : 'z-50';

  // Build overlay classes
  // Blocking: bg-black/80 scrim (no blur per spec); Non-blocking: transparent passthrough
  const overlayClasses = blocking
    ? `fixed inset-0 ${zIndex} flex items-center justify-center p-4 bg-black/80`
    : `fixed inset-0 ${zIndex} flex items-center justify-center p-4 pointer-events-none`;

  // Panel classes: pointer-events-auto for non-blocking so panel is still interactive
  const panelPointerEvents = blocking ? '' : 'pointer-events-auto';

  return (
    <div className={overlayClasses} onClick={handleOverlayClick}>
      <div
        className={`${maxWidthClass} w-full flex flex-col rounded-2xl shadow-2xl ${panelPointerEvents}`}
        style={{
          backgroundColor: MODAL_FRAME.bodyBg,
          borderWidth: `${MODAL_FRAME.borderWidthPx}px`,
          borderStyle: 'solid',
          borderColor: MODAL_FRAME.borderColor[variant],
          borderRadius: `${MODAL_FRAME.radiusPx}px`,
        }}
      >
        {/* Header (when title given) */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <div>
              <h2 className="text-2xl font-bold text-white">{title}</h2>
              {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
            </div>
            {shouldShowClose && onClose && <CloseButton onClick={onClose} />}
          </div>
        )}

        {/* Close button without header (for modals with custom body but still need close) */}
        {!title && shouldShowClose && onClose && (
          <div className="absolute top-4 right-4">
            <CloseButton onClick={onClose} />
          </div>
        )}

        {/* Body (caller's content) */}
        {children}
      </div>
    </div>
  );
}

export default Modal;
